import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';

interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
}

interface TagShareRow {
  shareId: number;
  sharedWithUserId: number;
  sharedWithUserName: string;
  permission: 'read' | 'write';
}

interface ShareTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  tagId: number | null;
  tagName: string;
  tagColor?: string | null;
  onShared: () => void;
}

const ShareTagModal: React.FC<ShareTagModalProps> = ({
  isOpen,
  onClose,
  tagId,
  tagName,
  tagColor,
  onShared,
}) => {
  const { colors } = useTheme();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [existingShares, setExistingShares] = useState<TagShareRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [permission, setPermission] = useState<'read' | 'write'>('write');
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || tagId == null) return;
    setError(null);
    setUsersLoading(true);
    const load = async () => {
      try {
        const [usersRes, sharesRes] = await Promise.all([
          api.get<UserOption[]>('/users'),
          api.get<TagShareRow[]>(`/emails/tags/${tagId}/shares`),
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        const shares = Array.isArray(sharesRes.data) ? sharesRes.data : [];
        setExistingShares(shares);
        setSelectedUserIds([]);
        setPermission('write');
      } catch {
        setUsers([]);
        setExistingShares([]);
      } finally {
        setUsersLoading(false);
      }
    };
    void load();
  }, [isOpen, tagId]);

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const userLabel = (u: UserOption) =>
    u.userDisplayName?.trim() ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ') ||
    u.userEmail ||
    `User #${u.id}`;

  const alreadySharedIds = new Set(existingShares.map((s) => s.sharedWithUserId));

  const handleSubmit = async () => {
    if (tagId == null || selectedUserIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const sharedWithUserId of selectedUserIds) {
        await api.post(`/emails/tags/${tagId}/share`, { sharedWithUserId, permission });
      }
      onShared();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to share tag');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (shareId: number) => {
    setRevokingId(shareId);
    setError(null);
    try {
      await api.delete(`/emails/tags/shares/${shareId}`);
      setExistingShares((prev) => prev.filter((s) => s.shareId !== shareId));
      onShared();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to revoke share');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Share tag</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        {tagName && (
          <div className={cn('flex items-center gap-2.5 px-4 py-3 rounded-xl mb-4 bg-black/5 dark:bg-white/5', colors.text)}>
            <span
              className={cn('w-3 h-3 rounded-full flex-shrink-0', !tagColor && 'bg-gray-300 dark:bg-gray-600')}
              style={tagColor ? { backgroundColor: tagColor } : undefined}
            />
            <div>
              <p className="text-xs font-medium uppercase tracking-wider opacity-60">Tag</p>
              <p className="text-sm font-medium truncate">{tagName}</p>
            </div>
          </div>
        )}

        {existingShares.length > 0 && (
          <div className="mb-4">
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Currently shared with</h3>
            <ul className={cn('rounded-xl border divide-y overflow-hidden', colors.border)}>
              {existingShares.map((share) => (
                <li key={share.shareId} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <span className={cn('truncate', colors.text)}>{share.sharedWithUserName}</span>
                  <span className={cn('text-xs shrink-0', colors.textMuted)}>
                    {share.permission === 'write' ? 'Can apply tag' : 'View only'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={revokingId === share.shareId}
                    disabled={revokingId != null}
                    onClick={() => void handleRevoke(share.shareId)}
                    className="text-red-600 hover:text-red-700 shrink-0"
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Share with more users</h3>
            <p className={cn('text-xs mb-3', colors.textMuted)}>
              Recipients can filter emails by this tag. &quot;Can apply tag&quot; also lets them add or remove the tag on messages.
            </p>

            {usersLoading ? (
              <div className={cn('flex items-center justify-center py-12', colors.textMuted)}>
                <span className="animate-pulse">Loading users…</span>
              </div>
            ) : (
              <div className={cn('rounded-xl border overflow-hidden max-h-52 overflow-y-auto', colors.border)}>
                {users
                  .filter((u) => !alreadySharedIds.has(u.id))
                  .map((u) => {
                    const selected = selectedUserIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 cursor-pointer border-b last:border-b-0',
                          colors.border,
                          selected && 'bg-indigo-50/50 dark:bg-indigo-900/20',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleUser(u.id)}
                          className="rounded border-gray-300"
                        />
                        <span className={cn('text-sm font-medium truncate', colors.text)}>{userLabel(u)}</span>
                      </label>
                    );
                  })}
              </div>
            )}
          </div>

          <div className={cn('flex items-center justify-between px-4 py-3 rounded-xl bg-black/5 dark:bg-white/5')}>
            <div>
              <p className={cn('text-sm font-medium', colors.text)}>Permission for new shares</p>
              <p className={cn('text-xs', colors.textMuted)}>Write allows applying the tag on emails</p>
            </div>
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value as 'read' | 'write')}
              className={cn('text-sm rounded-lg border px-2 py-1.5', colors.border, colors.surface, colors.text)}
            >
              <option value="read">View only</option>
              <option value="write">Can apply tag</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl px-4 py-3 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="default"
          onClick={() => void handleSubmit()}
          disabled={saving || selectedUserIds.length === 0 || tagId == null}
        >
          {saving ? 'Sharing…' : `Share with ${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShareTagModal;
